import type Client from '../client'
import type {
  ErrorAble,
  GiftcardResponse,
  GiftcardCreateRequest,
  GiftcardUpdateRequest,
  RequestItemBag,
  GiftcardTransaction,
  GiftcardRelation,
} from '@/api'
import PaginatedResponse from '../paginated-response'

export default class Giftcard {
  constructor(private client: Client) {}

  async go(
    page: number,
    query: RequestItemBag = {},
  ): Promise<PaginatedResponse<GiftcardResponse>> {
    return new PaginatedResponse(
      await this.client.get('giftcard', {
        query: { ...query, page },
      }),
    )
  }

  async add(data: GiftcardCreateRequest): Promise<ErrorAble<GiftcardResponse>> {
    return await this.client.post(`giftcard`, { data })
  }

  async update(
    id: number,
    data: GiftcardUpdateRequest & { order_detail_id?: number },
  ): Promise<ErrorAble<GiftcardResponse>> {
    return await this.client.put(`giftcard/${id}`, { data })
  }

  async redeem(
    id: number,
    experienced_at: Date | null = null,
    supplier_id: number | null = null,
    variation_id: number | null = null,
  ): Promise<ErrorAble<unknown>> {
    return await this.client.post('giftcard/redeem', {
      data: { id, experienced_at, supplier_id, variation_id },
    })
  }

  async revertExchangedToOriginal(
    code: string,
    order_id: number,
  ): Promise<ErrorAble<unknown>> {
    return await this.client.post('giftcard/revertExchangedToOriginal', {
      data: { code, order_id },
    })
  }

  async exchange(
    codes: number[],
    email: string,
    combine = false,
  ): Promise<ErrorAble<GiftcardResponse>> {
    return await this.client.post('giftcard/exchange', {
      data: { codes: codes, email: email, combine },
    })
  }

  async setNotUsed(ids: number[]): Promise<ErrorAble<GiftcardResponse>> {
    return await this.client.post('giftcard/setNotUsed', {
      data: { ids },
    })
  }

  async combine(
    codes: number[],
    email: string,
  ): Promise<ErrorAble<GiftcardResponse>> {
    return await this.client.post('giftcard/combine', {
      data: { codes: codes, email: email },
    })
  }

  async split(
    code: number,
    splitInto: number[],
  ): Promise<ErrorAble<GiftcardResponse>> {
    return await this.client.post(`giftcard/${code}/split`, {
      data: { split: splitInto },
    })
  }

  async transactions(
    giftcard: number,
    page = 1,
    query: RequestItemBag = {},
  ): Promise<PaginatedResponse<GiftcardTransaction>> {
    return new PaginatedResponse(
      await this.client.get(`giftcard/${giftcard}/transactions`, {
        query: { ...query, page },
      }),
    )
  }

  async relations(giftcard: number): Promise<PaginatedResponse<GiftcardRelation>> {
    return new PaginatedResponse(
      await this.client.get(`giftcard/${giftcard}/relations`),
    )
  }

  async triggerEvent(
    giftcard: number,
    event: string,
  ): Promise<ErrorAble<GiftcardResponse>> {
    return await this.client.post(`giftcard/${giftcard}/trigger-event`, {
      data: { event },
    })
  }

  async revertFromCart(giftcard: number): Promise<ErrorAble<GiftcardResponse>> {
    return await this.client.post(`giftcard/${giftcard}/revertFromCart`)
  }
}
